/**
 * Jazz 0.x implementation of `@diagram/collab-text`'s `UpdateLog`.
 *
 * The whole backend is "append to a CoList of immutable maps". There is no
 * conflict policy because there is nothing to conflict: entries are never
 * edited, so two writers appending concurrently produce two entries and both
 * survive. That is the property the whole phase rests on, and it is why this
 * port is the most portable piece of the design — the same shape works on a
 * relational table or a WebSocket relay.
 *
 * Lives in `jazz-schema` rather than in either consumer because both the
 * frontend and `fuse-mirror` need it, and it is meaningless without the
 * schema it reads.
 */
import type { Group } from "jazz-tools";
import {
  encodeInitialText,
  type AppendOptions,
  type LogEntry,
  type Unsubscribe,
  type UpdateLog,
} from "@diagram/collab-text";
import { TextUpdate, TextUpdateList } from "./schema.js";
import { base64ToBytes, bytesToBase64 } from "./base64.js";

/**
 * Creates a log whose single snapshot entry materialises to `initialText`.
 *
 * The seed and the list are created together, in one call, on purpose. Two
 * clients each seeding the same text produce two independent sets of Yjs
 * client ids, and applying both concatenates the text instead of merging it
 * — so the atomic unit has to be the whole log. Callers then assign it with
 * a single `set`, where last-write-wins leaves exactly one log standing.
 */
export function createTextUpdateLog(
  group: Group,
  initialText: string,
): TextUpdateList {
  const seed = TextUpdate.create(
    {
      data: bytesToBase64(encodeInitialText(initialText)),
      isSnapshot: true,
      createdAt: Date.now(),
    },
    { owner: group },
  );
  return TextUpdateList.create([seed], { owner: group });
}

export class JazzUpdateLog implements UpdateLog {
  /**
   * Decoded entries by CoValue id. Entries are immutable, so a decode is
   * valid forever — which matters because `getEntries` runs on every
   * notification and base64-decoding the whole log each time would make
   * every keystroke O(history).
   */
  private readonly decoded = new Map<string, LogEntry>();
  private snapshot: readonly LogEntry[] = [];
  /** Ids in `snapshot`, joined. Cheap test for "did the entry set move?". */
  private signature: string | null = null;
  /**
   * The signature as of the last notification. Separate from `signature`
   * because that one is a read cache that *any* `getEntries()` caller
   * refreshes — the port advertises `getEntries` as a cheap synchronous read
   * and supports several subscribers, so a second consumer reading between
   * the Jazz callback and `notifyIfChanged` would otherwise silence the
   * notification for everyone.
   */
  private notifiedSignature: string | null = null;
  private readonly listeners = new Set<() => void>();
  private unsubscribeJazz: (() => void) | null = null;

  /**
   * The owning group is passed in rather than read off `list.$jazz.owner`.
   * That accessor throws "CoValue has no owner" whenever the group carries a
   * member whose account is not loaded locally — which is the normal state
   * right after a project is shared, i.e. exactly the case this port exists
   * to serve. Every other Jazz adapter in the repo takes its group the same
   * way.
   */
  constructor(
    private readonly list: TextUpdateList,
    private readonly group: Group,
  ) {}

  /** Roles allowed to append to and prune the log. */
  private canWrite(): boolean {
    const role = this.group.myRole();
    return role === "writer" || role === "admin";
  }

  /**
   * The list this log reads. Consumers cache logs by it: a `FileEntry` can
   * have its `updates` field replaced (two writers bootstrapping the same
   * legacy file at once, last write winning), and a log bound to the losing
   * list would silently stop being read by anyone else.
   */
  get listId(): string {
    return this.list.$jazz.id;
  }

  getEntries(): readonly LogEntry[] {
    const entries: LogEntry[] = [];
    const ids: string[] = [];
    for (let i = 0; i < this.list.length; i++) {
      const item = this.list[i];
      // Null while the entry is still loading. Skipping it is safe: the
      // subscription fires again once it arrives, and Yjs updates can be
      // applied in any order.
      if (!item) continue;
      const id = item.$jazz.id;
      const entry = this.entryFor(id, item);
      // An entry whose payload will not decode is skipped rather than thrown
      // over. Throwing here would escape the Jazz subscription callback and,
      // because nothing is cached on the failure path, would do so again on
      // every subsequent read — one malformed entry would stop the file
      // syncing for the rest of the session.
      if (!entry) continue;
      ids.push(id);
      entries.push(entry);
    }

    const signature = ids.join(",");
    if (signature === this.signature) return this.snapshot;
    this.signature = signature;
    this.snapshot = entries;
    this.forgetPruned(new Set(ids));
    return entries;
  }

  // `async` rather than returning `Promise.resolve(...)`: creating the
  // CoValue and pushing it are real work that can throw — "CoValue has no
  // owner" while the group is still settling, for one — and a synchronous
  // throw here would escape the caller's `.catch` and land in the middle of a
  // Yjs update handler. `async` turns it into the rejection the port promises.
  // eslint-disable-next-line @typescript-eslint/require-await -- async is what converts a sync throw into a rejection
  async append(
    update: Uint8Array,
    options: AppendOptions = {},
  ): Promise<string | null> {
    if (!this.canWrite()) return null;
    const item = TextUpdate.create(
      {
        data: bytesToBase64(update),
        isSnapshot: options.isSnapshot ?? false,
        createdAt: Date.now(),
      },
      { owner: this.group },
    );
    this.list.$jazz.push(item);
    return item.$jazz.id;
  }

  // Async for the same reason as `append`: `$jazz.splice` can throw.
  // eslint-disable-next-line @typescript-eslint/require-await -- async is what converts a sync throw into a rejection
  async prune(entryIds: readonly string[]): Promise<void> {
    if (!this.canWrite()) return;
    const doomed = new Set(entryIds);
    if (doomed.size === 0) return;
    // Backwards, so a splice cannot shift an index yet to be visited.
    for (let i = this.list.length - 1; i >= 0; i--) {
      const item = this.list[i];
      if (item && doomed.has(item.$jazz.id)) this.list.$jazz.splice(i, 1);
    }
  }

  subscribe(callback: () => void): Unsubscribe {
    this.listeners.add(callback);
    if (!this.unsubscribeJazz) {
      // Baseline the gate on the entry set a consumer has already *read*, so
      // the first Jazz callback — which arrives whether or not anything moved
      // — is not reported as a change to a subscriber that has seen nothing.
      //
      // Deliberately not a fresh `getEntries()`: that would advance the read
      // cache past entries the consumer has not consumed, and the gate would
      // then report them as already seen. A `CollaborativeDocument` drains
      // and *then* subscribes, so it would silently never apply whatever
      // loaded in between.
      this.notifiedSignature = this.signature;
    }
    this.unsubscribeJazz ??= this.list.$jazz.subscribe(
      { resolve: { $each: true } },
      () => {
        this.notifyIfChanged();
      },
    );
    return () => {
      this.listeners.delete(callback);
      if (this.listeners.size > 0) return;
      this.unsubscribeJazz?.();
      this.unsubscribeJazz = null;
    };
  }

  /** Drop the log's Jazz subscription regardless of remaining listeners. */
  destroy(): void {
    this.listeners.clear();
    this.unsubscribeJazz?.();
    this.unsubscribeJazz = null;
  }

  /** The decoded entry, or undefined when its payload will not decode. */
  private entryFor(id: string, item: TextUpdate): LogEntry | undefined {
    const cached = this.decoded.get(id);
    if (cached) return cached;
    let update: Uint8Array;
    try {
      update = base64ToBytes(item.data);
    } catch (error) {
      console.error("jazz-schema: undecodable log entry", id, error);
      return undefined;
    }
    const entry: LogEntry = {
      id,
      update,
      isSnapshot: item.isSnapshot,
      createdAt: item.createdAt,
    };
    this.decoded.set(id, entry);
    return entry;
  }

  /** Release decoded entries that compaction removed from the list. */
  private forgetPruned(present: ReadonlySet<string>): void {
    if (this.decoded.size === present.size) return;
    for (const id of this.decoded.keys()) {
      if (!present.has(id)) this.decoded.delete(id);
    }
  }

  private notifyIfChanged(): void {
    this.getEntries();
    if (this.signature === this.notifiedSignature) return;
    this.notifiedSignature = this.signature;
    // Each listener is isolated: the port supports several, and one that
    // throws must not stop the rest from hearing about the change.
    for (const listener of [...this.listeners]) {
      try {
        listener();
      } catch (error) {
        console.error("jazz-schema: an update-log listener threw", error);
      }
    }
  }
}
